import { useEffect, useState } from "react";
import axios from "axios";
import { GraduationCap, Heart, Send, Check, X, User, Mail,
         Globe, Phone, Sparkles } from "lucide-react";

const AMOUNTS = [
  { fcfa: 500,   monthly: "≈ £0.66", eur: "≈ €0.76",  label: "🎒 Un cahier" },
  { fcfa: 1000,  monthly: "≈ £1.32", eur: "≈ €1.53",  label: "✏️ Une trousse" },
  { fcfa: 2000,  monthly: "≈ £2.65", eur: "≈ €3.05",  label: "📚 Livres" },
  { fcfa: 5000,  monthly: "≈ £6.61", eur: "≈ €7.63",  label: "🛡️ Impact fort" },
];
const DURATIONS = [
  { months: 1,  label: "1 mois (test)"        },
  { months: 3,  label: "3 mois (trimestre)"   },
  { months: 6,  label: "6 mois (semestre)"    },
  { months: 12, label: "12 mois (année ⭐)"   },
  { months: 24, label: "24 mois (biennale)"   },
];

export default function SponsorshipPublicSection({ API, lang = "fr" }) {
  const [children, setChildren] = useState([]);
  const [summary, setSummary] = useState({});
  const [selectedChild, setSelectedChild] = useState(null);
  const [form, setForm] = useState({
    sponsor_name: "", sponsor_email: "", sponsor_country: "",
    sponsor_phone: "", monthly_amount_fcfa: 500, commit_months: 12,
    payment_pref: "", message: "",
  });
  const [pledged, setPledged] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sponsorship/children`)
      .then(r => { setChildren(r.data.items || []); setSummary(r.data.summary || {}); })
      .catch(err => console.error(err));
  }, [API]);

  const openPledge = (child) => {
    setSelectedChild(child); setPledged(null);
    setForm(f => ({ ...f, monthly_amount_fcfa: child.monthly_need_fcfa || 500 }));
  };
  const closePledge = () => { setSelectedChild(null); setPledged(null); };

  const submitPledge = async () => {
    if (!form.sponsor_name.trim() || !form.sponsor_email.trim()) {
      alert("Nom et email obligatoires."); return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/sponsorship/pledge`, {
        child_id: selectedChild.id,
        sponsor_name: form.sponsor_name.trim(),
        sponsor_email: form.sponsor_email.trim(),
        sponsor_country: form.sponsor_country.trim() || null,
        sponsor_phone: form.sponsor_phone.trim() || null,
        monthly_amount_fcfa: Number(form.monthly_amount_fcfa),
        commit_months: Number(form.commit_months),
        payment_pref: form.payment_pref || null,
        message: form.message.trim() || null,
      });
      setPledged(r.data);
    } catch (e) {
      alert("❌ " + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };

  const T = {
    hero_title: "Parrainer un enfant, c'est écrire son avenir.",
    hero_sub: `À partir de <b>500 FCFA/mois</b> (≈ £0.66 · €0.76), vous financez directement la scolarité d'un enfant accompagné par SAFUAYE-TOGO. Aucune commission, 100% des dons vont à l'école.`,
    stats_children: "enfants à parrainer",
    stats_avail: "disponibles",
    stats_sponsored: "déjà parrainés",
    parrainer: "Parrainer",
    lets_do: "Je parraine cet enfant",
  };

  const totalPledge = Number(form.monthly_amount_fcfa) * Number(form.commit_months);

  return (
    <section id="parrainage" className="py-16 bg-gradient-to-br from-indigo-50 via-white to-amber-50"
             data-testid="parrainage-public-section">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-800 px-4 py-1 rounded-full text-sm font-semibold mb-4">
            <GraduationCap className="w-4 h-4" />
            Programme SAFUAYE — Parrainage Scolaire
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
            {T.hero_title}
          </h2>
          <p className="text-gray-700 max-w-2xl mx-auto"
             dangerouslySetInnerHTML={{ __html: T.hero_sub }} />

          {/* Impact stats */}
          <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mt-8">
            <div className="bg-white rounded-xl border-2 border-indigo-200 p-3">
              <div className="text-2xl font-bold text-indigo-700">{summary.total_children || 0}</div>
              <div className="text-xs text-gray-600">{T.stats_children}</div>
            </div>
            <div className="bg-white rounded-xl border-2 border-emerald-200 p-3">
              <div className="text-2xl font-bold text-emerald-700">{summary.available || 0}</div>
              <div className="text-xs text-gray-600">{T.stats_avail}</div>
            </div>
            <div className="bg-white rounded-xl border-2 border-amber-200 p-3">
              <div className="text-2xl font-bold text-amber-700">{summary.sponsored || 0}</div>
              <div className="text-xs text-gray-600">{T.stats_sponsored}</div>
            </div>
          </div>
        </div>

        {/* Children cards */}
        {children.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            Aucun enfant à parrainer pour le moment. Revenez très bientôt !
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {children.map(c => {
              const funded = Math.min(100, Math.round(
                ((c.total_received_fcfa || 0) / (c.yearly_target_fcfa || 1)) * 100));
              return (
                <div key={c.id}
                     className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow"
                     data-testid={`sponsor-child-card-${c.id}`}>
                  <div className="h-32 bg-gradient-to-br from-indigo-500 via-purple-500 to-amber-500 flex items-center justify-center text-white">
                    {c.photo_url ? (
                      <img src={c.photo_url} alt={c.display_name}
                           className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-16 h-16 opacity-70" />
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-heading text-lg font-bold text-gray-900">
                        {c.display_name || c.first_name}
                        {c.age ? <span className="text-sm text-gray-500 ml-2">· {c.age} ans</span> : null}
                      </h3>
                      {c.status === "available" ? (
                        <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-semibold">
                          Disponible
                        </span>
                      ) : c.status === "sponsored" ? (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-semibold">
                          Parrainé
                        </span>
                      ) : (
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-semibold">
                          Financé 100%
                        </span>
                      )}
                    </div>
                    {c.class_level && (
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-semibold">Classe :</span> {c.class_level}
                      </p>
                    )}
                    {c.school_name && (
                      <p className="text-sm text-gray-600 mb-3">🏫 {c.school_name}</p>
                    )}
                    {c.story && (
                      <p className="text-sm text-gray-700 mb-3 line-clamp-3">
                        {c.story}
                      </p>
                    )}

                    {/* Funding bar */}
                    <div className="mb-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">Financement annuel</span>
                        <span className="font-semibold text-indigo-700">
                          {(c.total_received_fcfa || 0).toLocaleString("fr-FR")} / {(c.yearly_target_fcfa || 0).toLocaleString("fr-FR")} FCFA
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div className="h-2 bg-gradient-to-r from-indigo-500 to-amber-500 rounded-full"
                             style={{ width: `${funded}%` }} />
                      </div>
                      <div className="text-xs text-right text-gray-500 mt-1">{funded}% couvert</div>
                    </div>

                    {c.status === "available" && (
                      <button onClick={() => openPledge(c)}
                              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 shadow-md"
                              data-testid={`sponsor-child-pledge-btn-${c.id}`}>
                        <Heart className="w-4 h-4" />
                        {T.parrainer} · dès {(c.monthly_need_fcfa || 500).toLocaleString("fr-FR")} FCFA/mois
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Trust bar */}
        <div className="mt-10 flex flex-wrap justify-center gap-4 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <Check className="w-4 h-4 text-emerald-600" /> 100% des dons vont à l'école
          </span>
          <span className="flex items-center gap-1">
            <Check className="w-4 h-4 text-emerald-600" /> Reçus mensuels de l'école
          </span>
          <span className="flex items-center gap-1">
            <Check className="w-4 h-4 text-emerald-600" /> Bulletin scolaire trimestriel
          </span>
          <span className="flex items-center gap-1">
            <Check className="w-4 h-4 text-emerald-600" /> Charity Commission UK
          </span>
        </div>
      </div>

      {/* Pledge Modal */}
      {selectedChild && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             data-testid="sponsor-pledge-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Parrainer {selectedChild.display_name || selectedChild.first_name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedChild.class_level && `Classe : ${selectedChild.class_level}`}
                  {selectedChild.school_name && ` · ${selectedChild.school_name}`}
                </p>
              </div>
              <button onClick={closePledge} className="text-gray-500 hover:text-gray-800"
                      data-testid="sponsor-pledge-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {pledged ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Merci du fond du cœur ! ❤️</h4>
                <p className="text-sm text-gray-700">{pledged.message}</p>
                <p className="text-xs text-gray-500 mt-3">Réf. engagement : <b>{pledged.pledge_id?.slice(0, 8)}...</b></p>
                <button onClick={closePledge}
                        className="mt-6 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded font-semibold">
                  Fermer
                </button>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                {/* Amount tiers */}
                <label className="block">
                  <span className="text-gray-700 font-semibold">Montant mensuel</span>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {AMOUNTS.map(a => {
                      const active = Number(form.monthly_amount_fcfa) === a.fcfa;
                      return (
                        <button key={a.fcfa} type="button"
                                onClick={() => setForm({ ...form, monthly_amount_fcfa: a.fcfa })}
                                className={`p-2 rounded-lg border-2 text-left ${
                                  active
                                    ? "bg-indigo-50 border-indigo-500"
                                    : "bg-white border-gray-200 hover:border-indigo-300"
                                }`}
                                data-testid={`sponsor-amount-${a.fcfa}`}>
                          <div className="font-bold text-gray-900">{a.fcfa.toLocaleString("fr-FR")} FCFA</div>
                          <div className="text-xs text-gray-600">{a.monthly} · {a.eur}</div>
                          <div className="text-xs text-indigo-700 mt-1">{a.label}</div>
                        </button>
                      );
                    })}
                  </div>
                  <input type="number" min="100" step="100" value={form.monthly_amount_fcfa}
                         onChange={e => setForm({ ...form, monthly_amount_fcfa: e.target.value })}
                         placeholder="Ou saisissez un montant libre (min 100 FCFA)"
                         className="w-full mt-2 px-3 py-2 border border-gray-300 rounded"
                         data-testid="sponsor-amount-custom" />
                </label>

                <label className="block">
                  <span className="text-gray-700 font-semibold">Durée d'engagement</span>
                  <select value={form.commit_months}
                          onChange={e => setForm({ ...form, commit_months: e.target.value })}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded"
                          data-testid="sponsor-duration">
                    {DURATIONS.map(d => (
                      <option key={d.months} value={d.months}>{d.label}</option>
                    ))}
                  </select>
                </label>

                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-600">Engagement total</div>
                  <div className="text-2xl font-bold text-indigo-700">
                    {totalPledge.toLocaleString("fr-FR")} FCFA
                  </div>
                  <div className="text-xs text-gray-500">
                    ≈ £{(totalPledge / 756).toFixed(2)} · €{(totalPledge / 655.96).toFixed(2)}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-gray-600 text-xs">Votre nom *</span>
                    <input value={form.sponsor_name}
                           onChange={e => setForm({ ...form, sponsor_name: e.target.value })}
                           placeholder="Prénom NOM"
                           className="w-full px-2 py-1.5 border border-gray-300 rounded"
                           data-testid="sponsor-name-input" />
                  </label>
                  <label className="block">
                    <span className="text-gray-600 text-xs">Email *</span>
                    <input type="email" value={form.sponsor_email}
                           onChange={e => setForm({ ...form, sponsor_email: e.target.value })}
                           className="w-full px-2 py-1.5 border border-gray-300 rounded"
                           data-testid="sponsor-email-input" />
                  </label>
                  <label className="block">
                    <span className="text-gray-600 text-xs">Pays</span>
                    <input value={form.sponsor_country}
                           onChange={e => setForm({ ...form, sponsor_country: e.target.value })}
                           placeholder="Royaume-Uni, France, Togo…"
                           className="w-full px-2 py-1.5 border border-gray-300 rounded" />
                  </label>
                  <label className="block">
                    <span className="text-gray-600 text-xs">Téléphone (optionnel)</span>
                    <input value={form.sponsor_phone}
                           onChange={e => setForm({ ...form, sponsor_phone: e.target.value })}
                           className="w-full px-2 py-1.5 border border-gray-300 rounded" />
                  </label>
                </div>

                <label className="block">
                  <span className="text-gray-600 text-xs">Modalité de paiement préférée</span>
                  <select value={form.payment_pref}
                          onChange={e => setForm({ ...form, payment_pref: e.target.value })}
                          className="w-full mt-1 px-2 py-1.5 border border-gray-300 rounded">
                    <option value="">— À définir ensemble —</option>
                    <option value="bank_transfer">Virement bancaire</option>
                    <option value="remitly">Remitly / Western Union</option>
                    <option value="cash">Espèces (Togo)</option>
                    <option value="later">Je choisirai plus tard</option>
                  </select>
                </label>

                <label className="block">
                  <span className="text-gray-600 text-xs">Message pour l'enfant (optionnel)</span>
                  <textarea value={form.message}
                            onChange={e => setForm({ ...form, message: e.target.value })}
                            rows={2}
                            placeholder="Un mot d'encouragement, vos coordonnées, une question…"
                            className="w-full mt-1 px-2 py-1.5 border border-gray-300 rounded" />
                </label>

                <button onClick={submitPledge} disabled={busy}
                        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 shadow-md"
                        data-testid="sponsor-pledge-submit">
                  <Send className="w-4 h-4" />
                  {busy ? "Envoi en cours…" : "Je m'engage à parrainer"}
                </button>
                <p className="text-xs text-gray-500 text-center">
                  Nous vous recontacterons sous 48h pour confirmer les modalités et démarrer le parrainage.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
